/**
 * Modal di registrazione e CORREZIONE di un pagamento/incasso su una fattura.
 *
 * Raccoglie: importo (default = residuo), data, metodo (da lookup configurabile),
 * tipo riferimento (manuale/banca/cassa), riferimento, note.
 * Il centro di costo NON si sceglie qui: appartiene al documento e si
 * eredita dalla fattura (vedi analitica.py).
 * Nuovo pagamento -> POST  /fatture/{id}/pagamenti
 * Correzione      -> PATCH /fatture/{id}/pagamenti/{pagamentoId}
 *
 * Prima esisteva solo la creazione: un importo sbagliato si poteva soltanto
 * cancellare e reinserire. Il tipo di riferimento non e' modificabile (cambierebbe
 * gli effetti su cassa e banca): in correzione il campo e' disabilitato.
 *
 * Import UI solo da @floraltech/ui.
 */
import { Button, Input, Label } from '@floraltech/ui';
import { useEffect, useMemo, useState } from 'react';

interface Metodo {
  id: string;
  codice: string;
  descrizione: string;
}

/** Pagamento gia' registrato, quando il modal e' in correzione. */
export interface PagamentoEsistente {
  id: string;
  importo: number | string;
  data_pagamento: string;
  modalita_codice: string | null;
  tipo_riferimento: string;
  riferimento: string | null;
  centro_costo_id: string | null;
  note: string | null;
}

interface Props {
  fatturaId: string;
  year: number;
  residuo: number;
  /**
   * Ritenuta d'acconto del documento e netto atteso. Servono a calcolare in
   * automatico la quota di ritenuta operata con questo pagamento: la norma dice
   * che la ritenuta si opera all'atto del pagamento ma non come si spezza un
   * acconto, quindi si propone la proporzione e si lascia correggere.
   * Assenti o a zero = la fattura non ha ritenuta e il campo non compare.
   */
  ritenutaDocumento?: number | null;
  ritenutaGiaOperata?: number | null;
  nettoAtteso?: number | null;
  /** Se presente, il modal corregge questo pagamento invece di crearne uno nuovo. */
  pagamento?: PagamentoEsistente | null;
  onClose: () => void;
  onSaved: () => void;
}

const oggi = () => new Date().toISOString().slice(0, 10);

/** Importo in centesimi: un dato fiscale non passa da un'aritmetica in virgola mobile. */
const inCentesimi = (testo: string): number | null => {
  const normalizzato = testo.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalizzato)) return null;
  return Math.round(Number(normalizzato) * 100);
};

const PagamentoFatturaModal: React.FC<Props> = ({
  fatturaId,
  year,
  residuo,
  ritenutaDocumento = null,
  ritenutaGiaOperata = null,
  nettoAtteso = null,
  pagamento = null,
  onClose,
  onSaved,
}) => {
  const inCorrezione = pagamento !== null;

  const [metodi, setMetodi] = useState<Metodo[]>([]);
  const [importo, setImporto] = useState<string>(
    pagamento
      ? Number(pagamento.importo).toFixed(2)
      : residuo > 0
        ? residuo.toFixed(2)
        : '',
  );
  const [data, setData] = useState<string>(pagamento?.data_pagamento ?? oggi());
  // Nessun default hardcoded: finche' la lookup non risponde il campo resta vuoto
  // e il salvataggio e' bloccato dalla validazione.
  const [modalitaCodice, setModalitaCodice] = useState<string>(
    pagamento?.modalita_codice ?? '',
  );
  const [tipoRiferimento, setTipoRiferimento] = useState<string>(
    pagamento?.tipo_riferimento ?? 'manuale',
  );
  const [riferimento, setRiferimento] = useState<string>(pagamento?.riferimento ?? '');
  const [note, setNote] = useState<string>(pagamento?.note ?? '');
  const [motivoRettifica, setMotivoRettifica] = useState<string>('');
  // Vuoto = usa il calcolo automatico. Si valorizza solo se l'utente lo tocca.
  const [ritenutaOperata, setRitenutaOperata] = useState<string>('');
  const [ritenutaToccata, setRitenutaToccata] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toccato, setToccato] = useState(false);

  const auth = () => ({
    Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  });

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch('/api/v1/fatture/lookups/metodi-pagamento', {
          headers: auth(),
        });
        if (r.ok) {
          const lista = (await r.json()) as Metodo[];
          setMetodi(lista);
          // Preseleziona il primo metodo CONFIGURATO, e solo se non c'e' gia' una
          // scelta: il valore viene dalla lookup, mai da una costante nel codice.
          setModalitaCodice((corrente) => corrente || lista[0]?.codice || '');
        }
      } catch {
        /* metodi opzionali */
      }
    })();
  }, []);

  // --- Ritenuta d'acconto ----------------------------------------------------
  const haRitenuta = !inCorrezione && !!ritenutaDocumento && ritenutaDocumento > 0;
  const residuoRitenuta = haRitenuta
    ? Math.round((ritenutaDocumento! - (ritenutaGiaOperata ?? 0)) * 100) / 100
    : 0;

  /**
   * Quota proporzionale all'importo pagato, con l'ultima che chiude per
   * differenza: e' la stessa regola che applica il backend, replicata qui solo
   * per MOSTRARE il numero prima di salvare. Se i due divergessero vince il
   * backend, che e' l'unico a conoscere i pagamenti gia' registrati.
   */
  const quotaProposta = (centesimiPagamento: number | null): string => {
    if (!haRitenuta || centesimiPagamento === null || !nettoAtteso || nettoAtteso <= 0)
      return '';
    const pagato = centesimiPagamento / 100;
    if (pagato >= residuo) return residuoRitenuta.toFixed(2);
    const quota = Math.round(((ritenutaDocumento! * pagato) / nettoAtteso) * 100) / 100;
    return Math.min(quota, residuoRitenuta).toFixed(2);
  };

  // Ricalcola finche' l'utente non tocca il campo: cosi' cambiando l'importo la
  // quota segue da sola, che e' il comportamento che ci si aspetta.
  useEffect(() => {
    if (!haRitenuta || ritenutaToccata) return;
    setRitenutaOperata(quotaProposta(inCentesimi(importo)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importo, haRitenuta, ritenutaToccata]);

  // --- Campi obbligatori -----------------------------------------------------
  const centesimi = inCentesimi(importo);
  const erroriCampi = useMemo(() => {
    const e: Record<string, string> = {};
    if (!importo.trim()) e.importo = "Indica l'importo.";
    else if (centesimi === null)
      e.importo = 'Importo non valido: usa cifre e al massimo due decimali.';
    else if (centesimi <= 0) e.importo = "L'importo deve essere maggiore di zero.";
    if (!data) e.data = 'Indica la data del pagamento.';
    if (!modalitaCodice) e.metodo = 'Scegli il metodo di pagamento.';
    // Solo in correzione: registrare un pagamento nuovo non e' una rettifica.
    if (inCorrezione && !motivoRettifica.trim())
      e.motivo = 'Indica il motivo della correzione: resta agli atti.';
    return e;
  }, [importo, centesimi, data, modalitaCodice, inCorrezione, motivoRettifica]);
  const valido = Object.keys(erroriCampi).length === 0;

  const mostra = (campo: string) => (toccato ? erroriCampi[campo] : undefined);

  const salva = async () => {
    setToccato(true);
    if (!valido) return;
    setSaving(true);
    setError(null);
    try {
      const corpo: Record<string, unknown> = {
        importo: (centesimi! / 100).toFixed(2),
        data_pagamento: data,
        modalita_codice: modalitaCodice || null,
        riferimento: riferimento.trim() || null,
        note: note.trim() || null,
      };
      // Il tipo di riferimento si manda solo in creazione: in correzione il
      // backend rifiuta un cambio, e rimandarlo uguale sarebbe solo rumore.
      if (!inCorrezione) corpo.tipo_riferimento = tipoRiferimento;
      // Obbligatorio in correzione: il backend lo pretende (migration 0016).
      if (inCorrezione) corpo.motivo_rettifica = motivoRettifica.trim();
      if (haRitenuta && ritenutaOperata.trim())
        corpo.ritenuta_operata = ritenutaOperata.trim();

      const url = inCorrezione
        ? `/api/v1/fatture/${fatturaId}/pagamenti/${pagamento!.id}?year=${year}`
        : `/api/v1/fatture/${fatturaId}/pagamenti?year=${year}`;
      const res = await fetch(url, {
        method: inCorrezione ? 'PATCH' : 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(
          typeof d.detail === 'string' ? d.detail : `Errore ${res.status}`,
        );
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const messaggioErrore = (testo?: string) =>
    testo ? <p className="text-xs text-destructive">{testo}</p> : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-900">
        <h2 className="mb-4 font-outfit text-xl font-semibold">
          {inCorrezione ? 'Correggi pagamento' : 'Registra pagamento'}
        </h2>

        {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="importo">
              Importo <span className="text-destructive">*</span>
            </Label>
            <Input
              id="importo"
              value={importo}
              onChange={(e) => setImporto(e.target.value)}
              onBlur={() => setToccato(true)}
              inputMode="decimal"
              aria-invalid={Boolean(mostra('importo'))}
            />
            {messaggioErrore(mostra('importo'))}
          </div>
          <div className="space-y-2">
            <Label htmlFor="data">
              Data pagamento <span className="text-destructive">*</span>
            </Label>
            <Input
              id="data"
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              onBlur={() => setToccato(true)}
              aria-invalid={Boolean(mostra('data'))}
            />
            {messaggioErrore(mostra('data'))}
          </div>
          <div className="space-y-2">
            <Label htmlFor="metodo">
              Metodo <span className="text-destructive">*</span>
            </Label>
            <select
              id="metodo"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={modalitaCodice}
              onChange={(e) => setModalitaCodice(e.target.value)}
              onBlur={() => setToccato(true)}
            >
              <option value="">- scegli -</option>
              {metodi.map((m) => (
                <option key={m.id} value={m.codice}>
                  {m.descrizione}
                </option>
              ))}
            </select>
            {messaggioErrore(mostra('metodo'))}
            {metodi.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Nessun metodo configurato: aggiungine uno nelle classificazioni.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="tipo">Riferimento</Label>
            <select
              id="tipo"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
              value={tipoRiferimento}
              disabled={inCorrezione}
              onChange={(e) => setTipoRiferimento(e.target.value)}
            >
              <option value="manuale">Manuale</option>
              <option value="banca">Movimento bancario</option>
              <option value="cassa">Cassa / contanti</option>
            </select>
            {inCorrezione && (
              <p className="text-xs text-muted-foreground">
                Non modificabile: per cambiarlo elimina il pagamento e registralo di nuovo.
              </p>
            )}
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="rif">Riferimento (testo)</Label>
            <Input
              id="rif"
              value={riferimento}
              onChange={(e) => setRiferimento(e.target.value)}
              placeholder="Es. Bonifico Intesa 15/06, n. assegno, ID transazione"
            />
          </div>
          {/* Il centro di costo NON si sceglie qui. Appartiene al documento, non
              al movimento di denaro: e' lo stesso costo e deve avere una sola
              imputazione. Finche' erano due campi indipendenti la stessa spesa
              risultava su due centri diversi (BITONTI 1065: GEN.EXP sulla
              fattura, GEN.SED sul pagamento), e due divergenze su tre erano lo
              stesso ramo preso a due livelli. Ora si eredita dalla fattura, e
              si cambia da li'. */}
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="note">Note</Label>
            <Input id="note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {haRitenuta && (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="ritenuta-operata">Ritenuta d'acconto trattenuta</Label>
              <Input
                id="ritenuta-operata"
                value={ritenutaOperata}
                onChange={(e) => {
                  setRitenutaToccata(true);
                  setRitenutaOperata(e.target.value);
                }}
                inputMode="decimal"
              />
              <p className="text-xs text-muted-foreground">
                Calcolata in automatico in proporzione all'importo pagato
                (documento: {ritenutaDocumento!.toFixed(2)}, gia' trattenuta:{' '}
                {(ritenutaGiaOperata ?? 0).toFixed(2)}). L'ultima tranche chiude per
                differenza, cosi' la somma resta esatta. Modificala se hai trattenuto
                un importo diverso. Va versata con F24, codice tributo 1040.
              </p>
            </div>
          )}
          {inCorrezione && (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="motivo-rettifica">Motivo della correzione *</Label>
              <Input
                id="motivo-rettifica"
                value={motivoRettifica}
                onChange={(e) => setMotivoRettifica(e.target.value)}
                placeholder="Perche' stai correggendo questo pagamento"
              />
              {messaggioErrore(mostra('motivo'))}
              <p className="text-xs text-muted-foreground">
                Resta agli atti: l'importo pagato determina lo stato fiscale della
                fattura.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annulla
          </Button>
          <Button onClick={() => void salva()} disabled={saving || (toccato && !valido)}>
            {saving
              ? 'Salvataggio...'
              : inCorrezione
                ? 'Salva correzione'
                : 'Registra pagamento'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PagamentoFatturaModal;
